import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (user && user !== false) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = mode === "login" ? await login(email, password) : await register(email, password, name);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    if (res.pending) {
      toast.success("Account created — awaiting admin approval");
      setMode("login");
      setPassword("");
      return;
    }
    toast.success(mode === "login" ? "Welcome back" : "Account created");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-stone-50">
      {/* Left: brand panel */}
      <div className="hidden lg:flex relative bg-emerald-950 overflow-hidden" data-testid="login-hero">
        {/* subtle radial accent */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.06),transparent_60%)]" />
        <div className="relative z-10 w-full flex flex-col items-center justify-center text-center px-12 text-stone-50">
          <img
            src="/logo.jpg"
            alt="Shah"
            className="w-56 h-56 rounded-2xl shadow-2xl ring-4 ring-stone-50/10 object-cover"
            data-testid="brand-logo-large"
          />
          <h1 className="mt-10 font-light text-5xl leading-tight tracking-tight">
            Manage rentals.<br />Mind your books.
          </h1>
          <p className="mt-4 text-stone-200/80 text-base max-w-md">
            A grounded place for your properties, tenants, documents, rent and GST — all in one folder.
          </p>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-8">
          <div className="lg:hidden">
            <Logo size={56} showWordmark vertical wordmarkClassName="text-emerald-900 text-xl" />
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
              {mode === "login" ? "Welcome back" : "Get started"}
            </p>
            <h2 className="text-3xl font-medium tracking-tight text-stone-900">
              {mode === "login" ? "Sign in to your account" : "Create your account"}
            </h2>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-stone-700">Name</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="auth-name-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-700">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="auth-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-stone-700">Password</Label>
              <Input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="auth-password-input" />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-900 hover:bg-emerald-800 text-stone-50 h-11 rounded-md group"
              data-testid="auth-submit-button"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
            </Button>
          </form>

          <button
            type="button"
            className="text-sm text-stone-600 hover:text-emerald-900 transition-colors"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            data-testid="auth-toggle-mode"
          >
            {mode === "login" ? "Don't have an account? Register" : "Already have an account? Sign in"}
          </button>

          {mode === "login" && (
            <button
              type="button"
              className="block text-sm text-stone-500 hover:text-emerald-900 transition-colors"
              onClick={() => setForgotOpen(true)}
              data-testid="forgot-password-link"
            >
              Forgot password?
            </button>
          )}
        </div>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
  );
}
